import React, { useState } from 'react';
import { invitationService } from '../services/invitationService';
import { MembershipRole, RateLimitError } from '../types';
import { InvitationResponse } from '../types/invitation';
import RateLimitModal from './RateLimitModal';

interface InviteMemberModalProps {
  shopId: string;
  onClose: () => void;
  onMemberInvited: (invitation: InvitationResponse) => void;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  shopId,
  onClose,
  onMemberInvited
}) => {
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<MembershipRole[]>(['VIEWER']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);

  const availableRoles: { role: MembershipRole; label: string }[] = [
    {
      role: 'ADMIN',
      label: 'Administrator'
    },
    {
      role: 'MEMBER',
      label: 'Member'
    },
    {
      role: 'VIEWER',
      label: 'Viewer'
    },
    {
      role: 'CLIENT',
      label: 'Client'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!email.trim()) {
      setError('Please enter an email address');
      setLoading(false);
      return;
    }

    try {
      const invitation = await invitationService.createInvitation(shopId, {
        email: email.trim(),
        roles: selectedRoles
      });

      onMemberInvited(invitation);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      if (error instanceof RateLimitError) {
        setRateLimitError(error);
      } else {
        // Prefer the server's message. "Please check the email and try again" is wrong for a plan
        // limit, which is what a PERSONAL_GUEST shop hits on its first invitation (CSAI-533).
        const message = error instanceof Error ? error.message : '';
        setError(message || 'Failed to send invitation. Please check the email and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ backgroundColor: 'var(--bg-primary)' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="heading-secondary text-xl">Invite Shop Member</h2>
          <button
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-banner mb-6">
              <div className="text-error">{error}</div>
            </div>
          )}

          {/* Email Input */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address *</label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter member's email address"
              autoComplete="email"
            />
            <div className="text-muted text-sm mt-1">
              An invitation email will be sent to this address
            </div>
          </div>

          {/* Role Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Assign Role</label>
            <p className="text-muted text-sm" style={{ marginBottom: '0.75rem' }}>
              Select a role for this member. You can change this later.
            </p>

            <div className="roles-list">
              {availableRoles.map(({ role, label }) => (
                <div key={role} className="role-option">
                  <label className="role-checkbox">
                    <input
                      type="radio"
                      name="role"
                      checked={selectedRoles.includes(role)}
                      onChange={() => setSelectedRoles([role])}
                      className="checkbox-input"
                    />
                    <div className="role-info">
                      <span className="font-semibold">{label}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost mr-3"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !email.trim() || selectedRoles.length === 0}
            >
              {loading ? 'Inviting...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>

      {/* Rate Limit Modal */}
      {rateLimitError && (
        <RateLimitModal
          error={rateLimitError}
          onClose={() => setRateLimitError(null)}
        />
      )}
    </div>
  );
};

export default InviteMemberModal;